"use strict";
/**
 * Context Types for Feature Flag Evaluation
 *
 * The feature flag system uses two types of context:
 * 1. User Context - Per-user attributes that affect flag evaluation
 * 2. System Context - Shared attributes that affect all users in an environment
 *
 * These contexts are stored in Firestore and propagated to clients for on-demand evaluation.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PartialUserContextSchema = exports.EvaluationContextSchema = exports.SystemContextSchema = exports.UserContextSchema = void 0;
exports.createUserContext = createUserContext;
exports.createSystemContext = createSystemContext;
const zod_1 = require("zod");
const definitions_1 = require("./definitions");
/**
 * User context schema - attributes about a specific user
 * Stored in appFlagsUserContext/{uid}
 */
exports.UserContextSchema = zod_1.z.object({
    /**
     * The user's UID (Firebase Auth UID)
     */
    userUid: zod_1.z.string(),
    /**
     * The user's email address
     */
    email: zod_1.z.string().optional(),
    /**
     * The user's handle (username)
     */
    handle: zod_1.z.string().optional(),
    /**
     * When the user was created (timestamp in ms)
     */
    createdAt: zod_1.z.number().optional(),
    /**
     * Whether the user has educational qualification
     */
    isEducational: zod_1.z.boolean().optional(),
    /**
     * The user's plan epoch (legacy, from_202410)
     */
    planEpoch: zod_1.z.string().optional(),
    /**
     * List of organization UIDs the user belongs to
     */
    organizationUids: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * Custom attributes for extensibility
     * Keys should be documented when used in flag rules
     */
    customAttributes: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
    /**
     * When this context was last updated
     */
    updatedAt: zod_1.z.number(),
    /**
     * Version counter for change detection
     */
    contextVersion: zod_1.z.number().default(1),
});
/**
 * System context schema - shared attributes for an environment
 * Stored in appFlagsSystemContext/{env}
 */
exports.SystemContextSchema = zod_1.z.object({
    /**
     * The environment this context is for
     */
    environment: definitions_1.FlagEnvironment,
    /**
     * Version counter for flag configs - incremented when any flag config changes
     */
    configVersion: zod_1.z.number().default(1),
    /**
     * Version counter for segments - incremented when any segment changes
     */
    segmentsVersion: zod_1.z.number().default(1),
    /**
     * When this context was last updated
     */
    updatedAt: zod_1.z.number(),
    /**
     * Custom attributes for extensibility
     */
    customAttributes: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
});
/**
 * Combined evaluation context - passed to the evaluation engine
 */
exports.EvaluationContextSchema = zod_1.z.object({
    /**
     * User-specific context
     */
    user: exports.UserContextSchema,
    /**
     * System/environment context
     */
    system: exports.SystemContextSchema,
});
/**
 * Partial user context for updates
 */
exports.PartialUserContextSchema = exports.UserContextSchema.partial().required({
    userUid: true,
    updatedAt: true,
});
/**
 * Helper to create an initial user context
 */
function createUserContext(userUid, attributes = {}) {
    return {
        userUid,
        organizationUids: [],
        customAttributes: {},
        contextVersion: 1,
        updatedAt: Date.now(),
        ...attributes,
    };
}
/**
 * Helper to create an initial system context
 */
function createSystemContext(environment) {
    return {
        environment,
        configVersion: 1,
        segmentsVersion: 1,
        updatedAt: Date.now(),
        customAttributes: {},
    };
}
//# sourceMappingURL=context.js.map